import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import DashboardLayout from '../components/DashboardLayout';
import TileCard from '../components/TileCard';
import InstallPrompt from '../components/InstallPrompt';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { api, getImageUrl } from '../utils/api';
import { toast } from 'sonner';
import { Loader2, Newspaper, Facebook, Linkedin, Instagram, Youtube, X, Link2, Calendar } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToPushNotifications, isPushNotificationSupported } from '../utils/pushNotifications';
import { countEnabledSocialLinks, socialLinkSizeClasses, SOCIAL_URL_KEYS } from '../lib/socialLinks';

// Custom TikTok icon component
const TikTokIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Custom Bluesky butterfly icon
const BlueSkyIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 568 501" fill="currentColor">
    <path d="M123.121 33.664C188.241 82.553 258.281 181.68 284 234.873c25.719-53.192 95.759-152.32 160.879-201.209C491.866-1.611 568-28.906 568 57.947c0 17.346-9.945 145.713-15.778 166.555-20.275 72.453-94.155 90.933-159.875 79.748C507.222 323.8 536.444 388.56 473.333 453.32c-119.86 122.992-172.272-30.859-185.702-70.281-2.462-7.227-3.614-10.608-3.631-7.74-.017-2.868-1.169.513-3.631 7.74-13.43 39.422-65.842 193.273-185.702 70.281-63.111-64.76-33.889-129.52 80.986-149.065-65.72 11.185-139.6-7.295-159.875-79.748C9.945 203.659 0 75.291 0 57.947 0-28.906 76.134-1.611 123.121 33.664Z"/>
  </svg>
);
export default function UserDashboard() {
  const [tiles, setTiles] = useState([]);
  const [articles, setArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [activeChatTile, setActiveChatTile] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gridCols, setGridCols] = useState(3);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Clear service worker cache on mount to get fresh data
    if ('caches' in window) {
      caches.keys().then(keys => {
        keys.forEach(key => caches.delete(key));
      });
    }
    fetchData();
    
    // Don't auto-subscribe to push notifications on load
    // User can enable via profile/settings if needed
  }, []);

  // Update page title dynamically
  useEffect(() => {
    if (customer) {
      document.title = customer.name || 'My Apps';
    }
  }, [customer]);

  const fetchData = async () => {
    try {
      const [tilesData, customerData] = await Promise.all([
        api.getTiles(),
        user?.customer_id
          ? api.getPublicCustomer(user.customer_id).catch(() =>
              api.getCustomer(user.customer_id).catch(() => null)
            )
          : Promise.resolve(null)
      ]);
      setTiles(tilesData);
      setCustomer(customerData);
      if (customerData?.grid_cols) {
        setGridCols(customerData.grid_cols);
      }
      // Fetch articles: use Nexus if enabled, otherwise manual articles
      const articleLimit = customerData?.news_article_count || 3;
      if (customerData?.nexus_articles_enabled && user?.customer_id) {
        const nexusData = await api.getPublicNexusArticles(user.customer_id, { limit: articleLimit }).catch(() => null);
        setArticles(nexusData?.articles || []);
      } else {
        const articlesData = await api.getTopArticles().catch(() => []);
        setArticles(articlesData);
      }
      // Fetch upcoming events inline if Nexus events enabled
      if (customerData?.nexus_events_enabled && user?.customer_id) {
        const now = new Date().toISOString().slice(0, 10);
        const eventsData = await api.getPublicNexusEvents(user.customer_id, { from: now, limit: 3 }).catch(() => null);
        setEvents(eventsData?.events || []);
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const socialMediaIcons = {
    facebook: { icon: Facebook, color: '#1877F2' },
    twitter: { icon: X, color: '#000000' },
    linkedin: { icon: Linkedin, color: '#0A66C2' },
    instagram: { icon: Instagram, color: '#E4405F' },
    youtube: { icon: Youtube, color: '#FF0000' },
    tiktok: { icon: TikTokIcon, color: '#000000' },
    bluesky: { icon: BlueSkyIcon, color: '#0085FF' },
    custom: { icon: Link2, color: '#334155' }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Apps">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const socialLinksSection = customer?.social_links_enabled && customer?.social_links && (() => {
    const enabledCount = countEnabledSocialLinks(customer.social_links);
    if (enabledCount === 0) return null;
    const sz = socialLinkSizeClasses(enabledCount);
    return (
      <Card className="border-none shadow-sm" style={{ backgroundColor: customer.social_background_color || customer.tile_color || '#FFFFFF' }} data-testid="social-links-card">
        <CardContent className="p-4 sm:p-6">
          {customer?.show_social_section_title !== false && customer?.social_section_title && (
            <h3 className="text-sm font-medium mb-4 text-center" style={{ color: customer.social_section_title_color || customer.font_color || '#1E293B' }}>
              {customer.social_section_title}
            </h3>
          )}
          <div className={`flex ${sz.gap} justify-center overflow-x-auto pb-2`} data-testid="social-links-row">
            {SOCIAL_URL_KEYS.map((platform) => {
              const url = customer.social_links[platform];
              const enabled = customer.social_links[`${platform}_enabled`];
              if (!url || !enabled || !socialMediaIcons[platform]) return null;
              const { icon: Icon, color } = socialMediaIcons[platform];
              const label = platform === 'custom'
                ? (customer.social_links.custom_label || 'Link')
                : platform;
              return (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={label}
                  aria-label={label}
                  className={`flex items-center justify-center ${sz.button} rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:-translate-y-1 active:scale-95 flex-shrink-0`}
                  style={{ color }}
                  data-testid={`social-link-${platform}`}
                >
                  <Icon className={sz.icon} />
                </a>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  })();

  const headerImageSection = customer?.header_image_url && (
    <div className="w-full mb-6">
      <img 
        src={customer.header_image_url} 
        alt="Header" 
        className="w-full h-auto object-contain"
      />
    </div>
  );

  const tilesSection = (
    <>
      {/* Show tiles section title only if toggle is enabled */}
      {customer?.show_tiles_section_title === true && customer?.tiles_section_title && (
        <div className="mb-6">
          <h3 className="text-base sm:text-lg font-semibold" style={{ color: customer?.tiles_section_title_color || customer?.font_color || '#1E293B' }}>
            {customer.tiles_section_title}
          </h3>
        </div>
      )}

        {tiles.length === 0 ? (
          <Card style={{ backgroundColor: customer?.tile_color || '#FFFFFF' }}>
            <CardContent className="py-12 text-center">
              <p style={{ color: customer?.font_color || '#1E293B' }}>No tiles available yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className={`grid ${
            gridCols === 2 ? 'grid-cols-2' : 
            gridCols === 3 ? 'grid-cols-3' : 
            'grid-cols-4'
          } gap-3 sm:gap-4`} data-testid="tiles-grid">
            {tiles.map((tile) => (
              <div key={tile.id} className={`tile-size-${customer?.tile_size || 'default'}`}>
                <TileCard 
                  tile={tile} 
                  tileColor={customer?.tile_color}
                  tileFontColor={customer?.tile_font_color}
                  tileFontSize={customer?.tile_font_size}
                  tileStyle={customer?.tile_style}
                  tileShape={customer?.tile_shape}
                  showTitle={customer?.show_tile_title !== false}
                  showIcon={customer?.show_tile_icon !== false}
                  primaryColor={customer?.primary_color}
                  onChatOpen={(tile) => setActiveChatTile(tile)}
                />
              </div>
            ))}
          </div>
        )}
    </>
  );

  const newsSection = customer?.news_enabled && articles.length > 0 && (
    <Card 
      className="border-l-4" 
      style={{ 
        borderLeftColor: customer?.primary_color || '#2563EB',
        backgroundColor: customer?.tile_color || '#FFFFFF'
      }} 
      data-testid="latest-article-card"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ color: customer?.primary_color || '#2563EB' }}>
          <Newspaper className="w-5 h-5" />
          Latest News
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {articles.map((article) => (
            <div key={article.id} className="space-y-3 pb-6 border-b last:border-b-0 last:pb-0">
              <div className="flex gap-4">
                {article.image_url && (
                  <img 
                    src={getImageUrl(article.image_url)} 
                    alt={article.title}
                    className="w-24 h-24 object-cover rounded flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-semibold mb-2 line-clamp-2" style={{ color: customer?.font_color || '#1E293B' }}>
                    {article.title}
                  </h4>
                  {article.excerpt && (
                    <p className="text-sm line-clamp-2 mb-2" style={{ color: customer?.font_color || '#64748B' }}>
                      {article.excerpt}
                    </p>
                  )}
                  <Button
                    variant="link"
                    className="h-auto p-0 text-sm font-medium font-sans"
                    style={{ color: customer?.primary_color || '#2563EB' }}
                    onClick={async () => {
                      // Match iOS: use list article if content already present, only fetch detail if missing
                      if (customer?.nexus_articles_enabled && user?.customer_id &&
                          !article.content_html && !article.body_html && !article.html &&
                          !article.content && !article.body) {
                        try {
                          const full = await api.getPublicNexusArticle(user.customer_id, article.id);
                          setSelectedArticle(full);
                        } catch {
                          setSelectedArticle(article);
                        }
                      } else {
                        setSelectedArticle(article);
                      }
                      try {
                        await api.trackAnalyticsEvent({
                          event_type: 'article_view',
                          article_id: article.id,
                          metadata: { article_title: article.title }
                        });
                      } catch (error) {
                        console.error('Failed to track article view:', error);
                      }
                    }}
                  >
                    Read more →
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const eventsSection = customer?.nexus_events_enabled ? (
    <Card
      className="border-l-4"
      style={{ borderLeftColor: customer?.primary_color || '#2563EB', backgroundColor: customer?.tile_color || '#FFFFFF' }}
      data-testid="events-section-card"
    >
      <CardHeader className="pb-2">
        <CardTitle
          className="flex items-center justify-between text-base"
          style={{ color: customer?.primary_color || '#2563EB' }}
        >
          <span className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Upcoming Events
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7 px-2"
            style={{ color: customer?.primary_color || '#2563EB' }}
            onClick={() => navigate('/events')}
            data-testid="events-view-all-btn"
          >
            View all
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {events.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">No upcoming events</p>
        ) : (
          <div className="space-y-2">
            {events.map(ev => (
              <button
                key={ev.id}
                data-testid={`event-preview-${ev.id}`}
                className="w-full flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left border border-gray-100"
                onClick={() => navigate('/events')}
              >
                <div
                  className="flex-shrink-0 w-10 text-center rounded-md py-1"
                  style={{ backgroundColor: `${customer?.primary_color || '#2563EB'}15` }}
                >
                  <p className="text-xs font-bold leading-none" style={{ color: customer?.primary_color || '#2563EB' }}>
                    {new Date(ev.start).toLocaleString('en-GB', { day: 'numeric' })}
                  </p>
                  <p className="text-xs leading-none mt-0.5" style={{ color: customer?.primary_color || '#2563EB' }}>
                    {new Date(ev.start).toLocaleString('en-GB', { month: 'short' })}
                  </p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight truncate" style={{ color: customer?.font_color || '#1E293B' }}>
                    {ev.title}
                  </p>
                  {ev.location && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{ev.location}</p>
                  )}
                  {!ev.all_day && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {new Date(ev.start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  ) : null;

  const renderContent = () => {
    const position = customer?.social_links_position || 'top';
    
    if (position === 'top') {
      return (
        <div className="space-y-8">
          {socialLinksSection}
          {tilesSection}
          {eventsSection}
          {newsSection}
        </div>
      );
    } else if (position === 'between') {
      return (
        <div className="space-y-8">
          {tilesSection}
          {socialLinksSection}
          {eventsSection}
          {newsSection}
        </div>
      );
    } else { // bottom
      return (
        <div className="space-y-8">
          {tilesSection}
          {eventsSection}
          {newsSection}
          {socialLinksSection}
        </div>
      );
    }
  };

  return (
    <DashboardLayout title="My Dashboard" headerImage={headerImageSection}>
      {renderContent()}
      <InstallPrompt />

      {/* Article Detail Dialog */}
      <Dialog open={!!selectedArticle} onOpenChange={(open) => !open && setSelectedArticle(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedArticle?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedArticle?.image_url && (
              <img 
                src={getImageUrl(selectedArticle.image_url)} 
                alt={selectedArticle.title}
                className="w-full h-64 object-cover rounded-lg"
              />
            )}
            {selectedArticle?.excerpt && (
              <p className="text-lg text-muted-foreground italic border-l-4 border-primary pl-4">
                {selectedArticle.excerpt}
              </p>
            )}
            <div className="max-w-none">
              {(selectedArticle?.content_html || selectedArticle?.body_html || selectedArticle?.html) ? (
                <div
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content_html || selectedArticle.body_html || selectedArticle.html }}
                  className="prose prose-sm max-w-none"
                />
              ) : (selectedArticle?.content || selectedArticle?.body) ? (
                (selectedArticle.content || selectedArticle.body).split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-4 text-foreground leading-relaxed">{paragraph}</p>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">No full content available for this article.</p>
              )}
            </div>
            {selectedArticle?.url && (
              <div className="pt-2">
                <a
                  href={selectedArticle.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium underline"
                  style={{ color: customer?.primary_color || '#2563EB' }}
                >
                  Read full article →
                </a>
              </div>
            )}
            <div className="text-sm text-muted-foreground pt-4 border-t">
              By {selectedArticle?.author} • {selectedArticle?.created_at && new Date(selectedArticle.created_at).toLocaleDateString()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ubots Chat Overlay — rendered via Portal so no parent z-index/stacking context can block it */}
      {activeChatTile && ReactDOM.createPortal((() => {
        const raw = activeChatTile.embed_code || activeChatTile.url || '';
        const match = raw.match(/ubots\.chat\/chat\/([a-f0-9-]{36})/i);
        const embedId = match ? match[1] : raw.replace('https://ubots.chat/chat/', '').trim();
        const chatUrl = `https://ubots.chat/chat/${embedId}`;
        return (
          <div
            className="fixed inset-0 flex flex-col"
            style={{ zIndex: 9999, backgroundColor: customer?.primary_color || '#2563EB' }}
            data-testid="chat-overlay"
          >
            <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
              <span className="text-white font-semibold text-base truncate">
                {activeChatTile.title || 'Chat'}
              </span>
              <button
                onClick={() => setActiveChatTile(null)}
                className="text-white opacity-80 hover:opacity-100 p-1 rounded-full"
                aria-label="Close chat"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <iframe
              src={chatUrl}
              title={activeChatTile.title || 'Chat'}
              className="flex-1 w-full border-0 bg-white"
              allow="microphone; camera"
            />
          </div>
        );
      })(), document.body)}
    </DashboardLayout>
  );
}